import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getBusinessList,
  creditBusiness,
  getBusinessWallet,
  getBusinessEmployees,
  createBusiness,
  updateBusiness,
  assignBusinessWorker,
  getClientsSimple
} from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Plus, MoreHorizontal, Users, DollarSign, UserPlus, Pencil } from 'lucide-react';
import { toast } from 'sonner';

const AdminBusinesses = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [businesses, setBusinesses] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [creditDialogOpen, setCreditDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [businessWallet, setBusinessWallet] = useState(null);
  const [employees, setEmployees] = useState([]);

  const [createForm, setCreateForm] = useState({
    email: '',
    password: '',
    full_name: '',
    business_name: '',
    phone: '',
    nipt: '',
    address: '',
    city: ''
  });

  const [editForm, setEditForm] = useState({
    full_name: '',
    business_name: '',
    phone: '',
    nipt: '',
    address: '',
    city: '',
    status: 'active'
  });

  const [assignForm, setAssignForm] = useState({
    client_id: '',
    employee_name: '',
    role: 'employee',
    monthly_limit_lek: ''
  });

  const [creditForm, setCreditForm] = useState({
    business_id: '',
    amount: '',
    reason: ''
  });

  const fetchBusinesses = async () => {
    try {
      setLoading(true);
      const response = await getBusinessList();
      setBusinesses(response.data.items || response.data.businesses || []);
    } catch (error) {
      console.error('Failed to fetch businesses:', error);
      toast.error('Gabim në ngarkimin e biznesesh');
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await getClientsSimple();
      const items = response.data.items || response.data.clients || response.data || [];
      setClients(Array.isArray(items) ? items : []);
    } catch (error) {
      setClients([]);
    }
  };

  useEffect(() => {
    if (!isAdmin) {
      navigate('/');
      return;
    }
    fetchBusinesses();
    fetchClients();
  }, [isAdmin, navigate]);

  if (!isAdmin) {
    return null;
  }

  const handleCreateBusiness = async (e) => {
    e.preventDefault();
    try {
      await createBusiness(createForm);
      toast.success('Biznesi u krijua me sukses');
      setCreateDialogOpen(false);
      setCreateForm({
        email: '',
        password: '',
        full_name: '',
        business_name: '',
        phone: '',
        nipt: '',
        address: '',
        city: ''
      });
      fetchBusinesses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gabim në krijimin e biznesit');
    }
  };

  const openEditDialog = (business) => {
    setSelectedBusiness(business);
    setEditForm({
      full_name: business.full_name || '',
      business_name: business.business_name || business.company_name || '',
      phone: business.phone || '',
      nipt: business.nipt || '',
      address: business.address || '',
      city: business.city || '',
      status: business.status || 'active'
    });
    setEditDialogOpen(true);
  };

  const handleEditBusiness = async (e) => {
    e.preventDefault();
    try {
      await updateBusiness(selectedBusiness.id, editForm);
      toast.success('Biznesi u përditësua');
      setEditDialogOpen(false);
      fetchBusinesses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gabim në përditësimin e biznesit');
    }
  };

  const openAssignDialog = (business) => {
    setSelectedBusiness(business);
    setAssignForm({
      client_id: '',
      employee_name: '',
      role: 'employee',
      monthly_limit_lek: ''
    });
    setAssignDialogOpen(true);
  };

  const handleAssignWorker = async (e) => {
    e.preventDefault();
    try {
      await assignBusinessWorker(selectedBusiness.id, {
        client_id: assignForm.client_id,
        employee_name: assignForm.employee_name || undefined,
        role: assignForm.role,
        monthly_limit_lek: assignForm.monthly_limit_lek ? parseFloat(assignForm.monthly_limit_lek) : undefined,
      });
      toast.success('Punëtori u caktua në biznes');
      setAssignDialogOpen(false);
      fetchBusinesses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gabim në caktimin e punëtorit');
    }
  };

  const handleCreditDialog = (business) => {
    setSelectedBusiness(business);
    setCreditForm({
      business_id: business.id,
      amount: '',
      reason: 'admin_credit'
    });
    setCreditDialogOpen(true);
  };

  const handleCredit = async (e) => {
    e.preventDefault();
    try {
      const response = await creditBusiness({
        business_id: creditForm.business_id,
        amount: parseFloat(creditForm.amount),
        reason: creditForm.reason
      });
      toast.success(`Biznesi u kredituà ${creditForm.amount} Kredite. Invoice: ${response.data.invoice_number}`);
      setCreditDialogOpen(false);
      fetchBusinesses();
    } catch (error) {
      console.error('Failed to credit business:', error);
      toast.error(error.response?.data?.detail || 'Gabim në kreditimin e biznesit');
    }
  };

  const handleViewDetails = async (business) => {
    setSelectedBusiness(business);
    try {
      const [walletRes, employeesRes] = await Promise.all([
        getBusinessWallet(business.id),
        getBusinessEmployees(business.id)
      ]);
      const walletData = walletRes.data.business_wallet || walletRes.data.wallet || walletRes.data;
      setBusinessWallet(walletData);
      setEmployees(employeesRes.data.items || employeesRes.data.employees || []);
      setDetailsDialogOpen(true);
    } catch (error) {
      console.error('Failed to fetch business details:', error);
      toast.error('Gabim në ngarkimin e detajeve');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('sq-AL', {
      style: 'currency',
      currency: 'ALL',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  if (loading) {
    return <div className="flex justify-center items-center h-screen">Ngacimi i të dhënave...</div>;
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Menaxhimi i Biznesesh</h1>
          <p className="text-gray-600">Kredia dhe menaxhim i llogave biznesi</p>
        </div>
        <Button onClick={() => setCreateDialogOpen(true)} className="gap-2">
          <Plus className="w-4 h-4" />
          Shto Biznes
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Listat e Biznesesh</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Emri i Biznesit</TableHead>
                <TableHead>Pronari</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Bilanci</TableHead>
                <TableHead>Punonjesit</TableHead>
                <TableHead>Statusi</TableHead>
                <TableHead>Aksione</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {businesses.map((business) => (
                <TableRow key={business.id}>
                                    <TableCell className="font-medium cursor-pointer hover:text-violet-400 transition-colors" onClick={() => navigate(`/admin-businesses/${business.id}`)}>
                                      {business.business_name || business.company_name}
                                    </TableCell>
                  <TableCell>{business.full_name}</TableCell>
                  <TableCell>{business.email}</TableCell>
                  <TableCell className="font-semibold text-green-600">
                    <div>{formatCurrency((business.wallet?.total_balance ?? business.wallet?.balance) || 0)}</div>
                    <div className="text-xs font-normal text-gray-500">
                      Biznesi: {formatCurrency((business.wallet?.business_main_balance ?? business.wallet?.balance) || 0)}
                    </div>
                    <div className="text-xs font-normal text-gray-500">
                      Karikimi: {formatCurrency(business.wallet?.charging_balance || 0)}
                    </div>
                  </TableCell>
                  <TableCell>{business.employee_count || business.employees_count || 0}</TableCell>
                  <TableCell>
                    <span className={`px-2 py-1 rounded text-xs font-semibold ${
                      (business.status || 'active') === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'
                    }`}>
                      {(business.status || 'active') === 'active' ? 'Aktiv' : business.status}
                    </span>
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEditDialog(business)}>
                          <Pencil className="w-4 h-4 mr-2" />
                          Edito Biznesin
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => openAssignDialog(business)}>
                          <UserPlus className="w-4 h-4 mr-2" />
                          Cakto Punëtor
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleCreditDialog(business)}>
                          <DollarSign className="w-4 h-4 mr-2" />
                          Kredia Biznesi
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleViewDetails(business)}>
                          <Users className="w-4 h-4 mr-2" />
                          Shiko Detaje
                                                <DropdownMenuItem onClick={() => navigate(`/admin-businesses/${business.id}`)}>
                                                  <Users className="w-4 h-4 mr-2" />
                                                  Hap Profilin
                                                </DropdownMenuItem>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Krijo Biznes të Ri</DialogTitle>
            <DialogDescription>Krijo llogari biznesi me kredencialet e veta.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateBusiness} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Email</Label>
                <Input type="email" value={createForm.email} onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })} required />
              </div>
              <div className="space-y-2">
                <Label>Fjalëkalimi</Label>
                <Input type="password" value={createForm.password} onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })} required />
              </div>
              <div className="space-y-2">
                <Label>Pronari</Label>
                <Input value={createForm.full_name} onChange={(e) => setCreateForm({ ...createForm, full_name: e.target.value })} required />
              </div>
              <div className="space-y-2">
                <Label>Emri i Biznesit</Label>
                <Input value={createForm.business_name} onChange={(e) => setCreateForm({ ...createForm, business_name: e.target.value })} required />
              </div>
              <div className="space-y-2">
                <Label>Telefon</Label>
                <Input value={createForm.phone} onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>NIPT</Label>
                <Input value={createForm.nipt} onChange={(e) => setCreateForm({ ...createForm, nipt: e.target.value })} />
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Adresë</Label>
                <Input value={createForm.address} onChange={(e) => setCreateForm({ ...createForm, address: e.target.value })} />
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Qyteti</Label>
                <Input value={createForm.city} onChange={(e) => setCreateForm({ ...createForm, city: e.target.value })} />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>Anulo</Button>
              <Button type="submit">Krijo Biznesin</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Ndrysho Biznesin</DialogTitle>
            <DialogDescription>Përditëso profilin e biznesit.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditBusiness} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Pronari</Label>
                <Input value={editForm.full_name} onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Emri i Biznesit</Label>
                <Input value={editForm.business_name} onChange={(e) => setEditForm({ ...editForm, business_name: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Telefon</Label>
                <Input value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>NIPT</Label>
                <Input value={editForm.nipt} onChange={(e) => setEditForm({ ...editForm, nipt: e.target.value })} />
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Adresë</Label>
                <Input value={editForm.address} onChange={(e) => setEditForm({ ...editForm, address: e.target.value })} />
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Qyteti</Label>
                <Input value={editForm.city} onChange={(e) => setEditForm({ ...editForm, city: e.target.value })} />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>Anulo</Button>
              <Button type="submit">Ruaj Ndryshimet</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cakto Punëtor te Biznesi</DialogTitle>
            <DialogDescription>Zgjidh klientin ekzistues për ta lidhur si punëtor.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAssignWorker} className="space-y-4">
            <div className="space-y-2">
              <Label>Klienti</Label>
              <select
                className="w-full h-10 border rounded-md px-3"
                value={assignForm.client_id}
                onChange={(e) => setAssignForm({ ...assignForm, client_id: e.target.value })}
                required
              >
                <option value="">Zgjidh klientin</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>{c.full_name || c.email} ({c.email})</option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label>Emri në Biznes (opsionale)</Label>
              <Input value={assignForm.employee_name} onChange={(e) => setAssignForm({ ...assignForm, employee_name: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Limiti Mujor (Kredite)</Label>
              <Input type="number" value={assignForm.monthly_limit_lek} onChange={(e) => setAssignForm({ ...assignForm, monthly_limit_lek: e.target.value })} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAssignDialogOpen(false)}>Anulo</Button>
              <Button type="submit">Cakto Punëtorin</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Credit Business Dialog */}
      <Dialog open={creditDialogOpen} onOpenChange={setCreditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Kredito Biznesin</DialogTitle>
            <DialogDescription>
              Shto kredi në llogarinë e {selectedBusiness?.business_name || selectedBusiness?.company_name}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCredit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="amount">Shuma (Kredite)</Label>
              <Input
                id="amount"
                type="number"
                step="0.01"
                placeholder="Shuma për kreditim"
                value={creditForm.amount}
                onChange={(e) => setCreditForm({ ...creditForm, amount: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="reason">Arsyeja</Label>
              <Input
                id="reason"
                placeholder="Arsyeja e kreditimit"
                value={creditForm.reason}
                onChange={(e) => setCreditForm({ ...creditForm, reason: e.target.value })}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreditDialogOpen(false)}>
                Anulo
              </Button>
              <Button type="submit">Kredito</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Business Details Dialog */}
      <Dialog open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{selectedBusiness?.business_name || selectedBusiness?.company_name} - Detaje</DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            {/* Wallet Summary */}
            <Card className="bg-gradient-to-r from-blue-50 to-indigo-50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="w-5 h-5" />
                  Përmbledhja e Portofolit
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Bilanci Total</p>
                  <p className="text-2xl font-bold text-green-600">
                    {formatCurrency((businessWallet?.total_balance ?? businessWallet?.balance) || 0)}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Biznesi: {formatCurrency((businessWallet?.business_main_balance ?? businessWallet?.balance) || 0)}
                  </p>
                  <p className="text-xs text-gray-500">
                    Karikimi: {formatCurrency(businessWallet?.charging_balance || 0)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Alokuar te Punonjesit</p>
                  <p className="text-2xl font-bold text-yellow-600">
                    {formatCurrency(businessWallet?.allocated_total || 0)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Shpenzuar</p>
                  <p className="text-2xl font-bold text-red-600">
                    {formatCurrency(businessWallet?.spent || 0)}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Employees */}
            <div>
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <Users className="w-4 h-4" />
                Punojnesit ({employees.length})
              </h3>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Emri</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Statusi</TableHead>
                    <TableHead>Bilanci i Alokauar</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {employees.map((emp) => (
                    <TableRow key={emp.id}>
                      <TableCell>{emp.employee_name}</TableCell>
                      <TableCell>{emp.employee_email}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${
                          emp.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {emp.status === 'active' ? 'Aktiv' : 'Në Pritje'}
                        </span>
                      </TableCell>
                      <TableCell className="font-semibold">
                        {formatCurrency(emp.wallet?.balance || 0)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminBusinesses;
